import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Treemap,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import dayjs from 'dayjs';
import type { Expense, ExpenseFilter } from '../../../types/expense';
import type { CategoryOption } from '../../../constants/khaataCategories';
import { getExpenseType } from '../../../types/expense';
import {
  KHAATA_TYPES,
  TYPE_FIELD_CONFIG,
  formatCategoryLabel,
  getTypeLabel,
} from '../../../constants/khaataCategories';

/** Bucket granularity for "Expenses Over Time" based on period / custom range */
type TimeBucketType = 'daily' | 'weekly' | 'monthly' | 'quarterly';

const CHART_COLORS = [
  '#0088FE',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#8884D8',
  '#82ca9d',
  '#ffc658',
  '#a4de6c',
  '#8dd1e1',
];

// ---------------------------------------------------------------------------
// Helpers: aggregate expenses for charts
// ---------------------------------------------------------------------------

function aggregateByCategory(
  expenses: Expense[],
  categoryOptions?: CategoryOption[]
): { name: string; value: number; amount: number; percentage?: number }[] {
  const byCat: Record<string, number> = {};
  expenses.forEach((e) => {
    const cat = e.categoryLabel || formatCategoryLabel(e.category, categoryOptions);
    byCat[cat] = (byCat[cat] ?? 0) + e.amount;
  });
  const total = expenses.reduce((s, e) => s + e.amount, 0);
  return Object.entries(byCat)
    .map(([name, value]) => ({
      name,
      value,
      amount: value,
      percentage: total > 0 ? Math.round((value / total) * 100) : 0,
    }))
    .sort((a, b) => b.value - a.value);
}

/**
 * Chooses time-bucket granularity from filter:
 * - Monthly view (This Month) → weekly
 * - Yearly view → monthly
 * - Custom range: ≤31d daily, ≤6mo weekly, ≤3y monthly, >3y quarterly
 */
function getTimeBucketType(filter: ExpenseFilter): TimeBucketType {
  if (filter.period === 'monthly') return 'weekly';
  if (filter.period === 'yearly') return 'monthly';
  if (filter.period === 'daily') return 'daily';
  if (filter.period === 'weekly') return 'daily';
  if (filter.period === 'custom' && filter.fromDate && filter.toDate) {
    const from = dayjs(filter.fromDate);
    const to = dayjs(filter.toDate);
    const days = to.diff(from, 'day') + 1;
    if (days <= 31) return 'daily';
    if (days <= 180) return 'weekly';   // ~6 months
    if (days <= 1095) return 'monthly'; // 3 years
    return 'quarterly';
  }
  return 'weekly';
}

export interface TimeBucketItem {
  label: string;
  amount: number;
  sortKey: string;
}

/**
 * Aggregates expenses into time buckets (daily / weekly / monthly / quarterly)
 * for the "Expenses Over Time" chart based on current filter.
 */
function aggregateExpensesOverTime(
  expenses: Expense[],
  bucketType: TimeBucketType
): TimeBucketItem[] {
  const buckets: Record<string, number> = {};
  const labelsByKey = new Map<string, string>();

  expenses.forEach((e) => {
    const d = dayjs(e.date);
    let key: string;
    let label: string;

    switch (bucketType) {
      case 'daily':
        key = e.date;
        label = d.format('D MMM');
        break;
      case 'weekly': {
        const start = d.startOf('week');
        const end = d.endOf('week');
        key = start.format('YYYY-MM-DD');
        label = `${start.format('D MMM')} - ${end.format('D MMM')}`;
        break;
      }
      case 'monthly':
        key = d.format('YYYY-MM');
        label = d.format('MMM YYYY');
        break;
      case 'quarterly': {
        const q = Math.floor(d.month() / 3) + 1;
        key = `${d.year()}-Q${q}`;
        label = `Q${q} ${d.year()}`;
        break;
      }
      default:
        key = e.date;
        label = d.format('D MMM');
    }

    buckets[key] = (buckets[key] ?? 0) + e.amount;
    if (!labelsByKey.has(key)) labelsByKey.set(key, label);
  });

  return Object.entries(buckets)
    .map(([key, amount]) => ({
      label: labelsByKey.get(key) ?? key,
      amount,
      sortKey: key,
    }))
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey));
}

function aggregateByType(expenses: Expense[]): { name: string; value: number }[] {
  const byType: Record<string, number> = {};
  expenses.forEach((e) => {
    const key = getTypeLabel(getExpenseType(e));
    byType[key] = (byType[key] ?? 0) + e.amount;
  });
  return KHAATA_TYPES.map((t) => ({ name: t.label, value: byType[t.label] ?? 0 })).filter(
    (item) => item.value > 0
  );
}

/** Suppliers appear on purchase and asset entries. */
const supplierTypes = KHAATA_TYPES.filter((t) => TYPE_FIELD_CONFIG[t.value].supplier).map(
  (t) => t.value
);

function aggregateBySupplier(expenses: Expense[]): { name: string; value: number }[] {
  const bySupplier: Record<string, number> = {};
  expenses
    .filter((e) => supplierTypes.includes(getExpenseType(e)))
    .forEach((e) => {
      const name = e.supplierName || 'Unknown Supplier';
      bySupplier[name] = (bySupplier[name] ?? 0) + e.amount;
    });
  return Object.entries(bySupplier)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

function aggregateByPaymentMethod(expenses: Expense[]): { name: string; value: number }[] {
  const labels: Record<string, string> = {
    cash: 'Cash',
    card: 'Card',
    upi: 'UPI',
    bank_transfer: 'Bank Transfer',
    pending: 'Pending',
  };
  const byMethod: Record<string, number> = {};
  expenses.forEach((e) => {
    const key = e.paymentMethod || 'cash';
    byMethod[key] = (byMethod[key] ?? 0) + e.amount;
  });
  return Object.entries(byMethod).map(([key, value]) => ({
    name: labels[key] || key,
    value,
  }));
}

// ---------------------------------------------------------------------------
// Chart card wrapper
// ---------------------------------------------------------------------------

const ChartCard: React.FC<{
  title: string;
  children: React.ReactNode;
  minHeight?: number | Record<string, number>;
}> = ({ title, children, minHeight = 280 }) => (
  <Paper
    sx={{
      p: { xs: 1.5, sm: 2, md: 3 },
      borderRadius: 2,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
      overflow: 'hidden',
    }}
  >
    <Typography
      variant="h6"
      gutterBottom
      sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}
    >
      {title}
    </Typography>
    <Box sx={{ flex: 1, minHeight: typeof minHeight === 'object' ? minHeight : minHeight }}>
      {children}
    </Box>
  </Paper>
);

// ---------------------------------------------------------------------------
// Treemap: category-wise expenses
// ---------------------------------------------------------------------------

const TreemapContent: React.FC<any> = ({ x, y, width, height, name, value, index }) => {
  if (!name || width < 4 || height < 4) return null;
  const fill = CHART_COLORS[(index ?? 0) % CHART_COLORS.length];
  const showLabel = width > 50 && height > 32;
  const showValue = width > 50 && height > 48;
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        stroke="#fff"
        strokeWidth={2}
        rx={4}
        ry={4}
      />
      {showLabel && (
        <text
          x={x + width / 2}
          y={y + height / 2 - (showValue ? 8 : 0)}
          textAnchor="middle"
          dominantBaseline="central"
          fill="#fff"
          fontSize={Math.min(13, width / 8)}
          fontWeight={600}
        >
          {name}
        </text>
      )}
      {showValue && (
        <text
          x={x + width / 2}
          y={y + height / 2 + 10}
          textAnchor="middle"
          dominantBaseline="central"
          fill="rgba(255,255,255,0.9)"
          fontSize={Math.min(11, width / 10)}
        >
          ₹{Number(value).toLocaleString()}
        </text>
      )}
    </g>
  );
};

const TreemapTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <Paper sx={{ p: 1.5, minWidth: 140, maxWidth: '85vw', boxShadow: 3 }}>
      <Typography variant="subtitle2" fontWeight={600}>
        {d.name}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        ₹{Number(d.value).toLocaleString()}
        {d.percentage !== null && d.percentage !== undefined && ` (${d.percentage}%)`}
      </Typography>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// ExpenseAnalytics
// ---------------------------------------------------------------------------

export interface ExpenseAnalyticsProps {
  expenses: Expense[];
  filter: ExpenseFilter;
  categoryOptions?: CategoryOption[];
}

const ExpenseAnalyticsComponent: React.FC<ExpenseAnalyticsProps> = ({
  expenses,
  filter,
  categoryOptions,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const categoryData = useMemo(
    () => aggregateByCategory(expenses, categoryOptions),
    [expenses, categoryOptions]
  );
  const typeData = useMemo(() => aggregateByType(expenses), [expenses]);
  const supplierData = useMemo(() => aggregateBySupplier(expenses), [expenses]);
  const timeBucketType = useMemo(() => getTimeBucketType(filter), [filter]);
  const expensesOverTimeData = useMemo(
    () => aggregateExpensesOverTime(expenses, timeBucketType),
    [expenses, timeBucketType]
  );
  const paymentData = useMemo(() => aggregateByPaymentMethod(expenses), [expenses]);

  const timeBucketSubtitle =
    timeBucketType === 'daily'
      ? 'by day'
      : timeBucketType === 'weekly'
        ? 'by week'
        : timeBucketType === 'monthly'
          ? 'by month'
          : 'by quarter';

  const totalAmount = expenses.reduce((s, e) => s + e.amount, 0);
  const typeBreakdown = useMemo(() => {
    return KHAATA_TYPES.map((t) => {
      const rows = expenses.filter((e) => getExpenseType(e) === t.value);
      return {
        label: t.label,
        count: rows.length,
        total: rows.reduce((s, e) => s + e.amount, 0),
      };
    }).filter((b) => b.count > 0);
  }, [expenses]);
  const hasMultipleTypes = typeBreakdown.length > 1;
  const dateRangeLabel =
    filter.period === 'custom' && filter.fromDate && filter.toDate
      ? `${filter.fromDate} to ${filter.toDate}`
      : filter.period === 'yearly'
        ? String(filter.year)
        : filter.period === 'daily'
          ? 'Today'
          : filter.period === 'weekly'
            ? 'This Week'
            : filter.period === 'monthly'
              ? 'This Month'
              : 'Selected period';

  if (expenses.length === 0) {
    return (
      <Box
        sx={{
          py: { xs: 4, sm: 6 },
          px: { xs: 1.5, sm: 0 },
          textAlign: 'center',
          color: 'text.secondary',
          borderRadius: 2,
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant="body1" sx={{ fontSize: { xs: '0.9375rem', sm: '1rem' } }}>
          No entries in the selected date range.
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
          Switch to Table view to add expenses or purchases, or change the filters.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 }, mb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: 'wrap',
          gap: { xs: 0.5, sm: 2 },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="subtitle1"
          color="text.secondary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {dateRangeLabel} · {expenses.length} entr{expenses.length !== 1 ? 'ies' : 'y'}
          {hasMultipleTypes
            ? ` (${typeBreakdown.map((b) => `${b.count} ${b.label.toLowerCase()}`).join(', ')})`
            : ''}
        </Typography>
        <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
          <Typography
            variant="h6"
            fontWeight={700}
            color="primary.main"
            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            Total: ₹{totalAmount.toLocaleString()}
          </Typography>
          {hasMultipleTypes && (
            <Typography variant="caption" color="text.secondary" display="block">
              {typeBreakdown
                .map((b) => `${b.label} ₹${b.total.toLocaleString()}`)
                .join(' · ')}
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 2, md: 3 },
        }}
      >
        {/* Breakdown by type */}
        {typeData.length > 1 && (
          <ChartCard title="By Type" minHeight={{ xs: 260, sm: 280, md: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={typeData}
                  cx="50%"
                  cy="50%"
                  innerRadius={isMobile ? 40 : 60}
                  outerRadius={isMobile ? 65 : 90}
                  paddingAngle={2}
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {typeData.map((_, index) => (
                    <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip formatter={(value: number) => [`₹${value.toLocaleString()}`, 'Amount']} />
                <Legend wrapperStyle={{ fontSize: isMobile ? 11 : 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {/* Category-wise Treemap */}
        <ChartCard title="By Category" minHeight={{ xs: 260, sm: 280, md: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <Treemap
              data={categoryData}
              dataKey="value"
              nameKey="name"
              aspectRatio={4 / 3}
              content={<TreemapContent />}
            >
              <RechartsTooltip content={<TreemapTooltip />} cursor={false} />
            </Treemap>
          </ResponsiveContainer>
        </ChartCard>

        {/* Payment method distribution */}
        <ChartCard title="Payment Method" minHeight={{ xs: 260, sm: 280, md: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={paymentData}
                cx="50%"
                cy="50%"
                innerRadius={isMobile ? 40 : 60}
                outerRadius={isMobile ? 65 : 90}
                paddingAngle={2}
                dataKey="value"
                nameKey="name"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {paymentData.map((_, index) => (
                  <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <RechartsTooltip formatter={(value: number) => [`₹${value.toLocaleString()}`, 'Amount']} />
              <Legend wrapperStyle={{ fontSize: isMobile ? 11 : 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </Box>

      {/* Top suppliers for purchases */}
      {supplierData.length > 0 && (
        <ChartCard title="Top Suppliers" minHeight={{ xs: 220, sm: 260, md: 280 }}>
          <Box sx={{ width: '100%', height: { xs: 220, sm: 260, md: 280 } }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={supplierData.slice(0, 8)}
                margin={{
                  top: 8,
                  right: isMobile ? 8 : 24,
                  left: isMobile ? 72 : 100,
                  bottom: 8,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.grey[200]} />
                <XAxis
                  type="number"
                  tick={{ fontSize: isMobile ? 9 : 12 }}
                  tickFormatter={(v) => `₹${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={isMobile ? 72 : 96}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <RechartsTooltip
                  formatter={(value: number) => [`₹${value.toLocaleString()}`, 'Amount']}
                  contentStyle={{ maxWidth: isMobile ? '90vw' : undefined }}
                />
                <Bar dataKey="value" name="Amount" fill={theme.palette.info.main} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </ChartCard>
      )}

      {/* Expenses over time (bar chart) - granularity by period: weekly for monthly, monthly for yearly, etc. */}
      <ChartCard title={`Spending Over Time (${timeBucketSubtitle})`} minHeight={{ xs: 260, sm: 300 }}>
        <Box sx={{ width: '100%', height: { xs: 260, sm: 300 } }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={expensesOverTimeData}
              margin={{
                top: 8,
                right: isMobile ? 4 : 16,
                left: isMobile ? 0 : 8,
                bottom: isMobile ? 20 : 24,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.grey[200]} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: isMobile ? 9 : 11 }}
                stroke={theme.palette.text.secondary}
                interval={expensesOverTimeData.length > (isMobile ? 8 : 12) ? Math.ceil(expensesOverTimeData.length / (isMobile ? 8 : 12)) : 0}
              />
              <YAxis
                tick={{ fontSize: isMobile ? 10 : 12 }}
                stroke={theme.palette.text.secondary}
                tickFormatter={(v) => `₹${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
                width={isMobile ? 36 : 48}
              />
              <RechartsTooltip
                formatter={(value: number) => [`₹${value.toLocaleString()}`, 'Amount']}
                contentStyle={{ borderRadius: 8, maxWidth: isMobile ? '90vw' : undefined }}
              />
              <Bar
                dataKey="amount"
                name="Amount"
                fill={theme.palette.primary.main}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </ChartCard>

      {/* Top categories bar (horizontal) - explicit height so ResponsiveContainer can measure */}
      {categoryData.length > 0 && (
        <ChartCard title="Top Categories by Amount" minHeight={{ xs: 220, sm: 260, md: 280 }}>
          <Box sx={{ width: '100%', height: { xs: 220, sm: 260, md: 280 } }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={categoryData.slice(0, 8)}
                margin={{
                  top: 8,
                  right: isMobile ? 8 : 24,
                  left: isMobile ? 52 : 80,
                  bottom: 8,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.grey[200]} />
                <XAxis
                  type="number"
                  tick={{ fontSize: isMobile ? 9 : 12 }}
                  tickFormatter={(v) => `₹${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={isMobile ? 52 : 72}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <RechartsTooltip
                  formatter={(value: number) => [`₹${value.toLocaleString()}`, 'Amount']}
                  contentStyle={{ maxWidth: isMobile ? '90vw' : undefined }}
                />
                <Bar dataKey="value" name="Amount" fill={theme.palette.secondary.main} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </ChartCard>
      )}
    </Box>
  );
};

export const ExpenseAnalytics = React.memo(ExpenseAnalyticsComponent);

export default ExpenseAnalytics;
